import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  AppBar,
  Toolbar,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  IconButton,
  InputAdornment,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useSafeBack } from '../hooks/useSafeBack';
import userService, { UserDTO, CreateUserRequest } from '../services/userService';
import { toast } from 'react-toastify';
import { EnhancedAddUserDialog } from '../components/EnhancedAddUserDialog';

const UserManagementPage: React.FC = () => {
  const { user, logout, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  
  // Data state
  const [students, setStudents] = useState<UserDTO[]>([]);
  const [providers, setProviders] = useState<UserDTO[]>([]);
  const [admins, setAdmins] = useState<UserDTO[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Filtering state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [universityFilter, setUniversityFilter] = useState<number | 'all'>('all');
  const [universities, setUniversities] = useState<any[]>([]);
  
  // Pagination state
  const [studentsPage, setStudentsPage] = useState(1);
  const [providersPage, setProvidersPage] = useState(1);
  const [adminsPage, setAdminsPage] = useState(1);
  const ITEMS_PER_PAGE = 10;
  

  const handleLogout = () => {
    logout();
  };

  const goBack = useSafeBack();
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    try {
      setLoading(true);
      const [studentsData, providersData, adminsData, universitiesData] = await Promise.all([
        userService.getStudents(),
        userService.getProviders(),
        userService.getAdmins(),
        userService.getUniversities()
      ]);
      
      setStudents(studentsData);
      setProviders(providersData);
      setAdmins(adminsData);
      setUniversities(universitiesData);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };
  
  
  const handleUpdateUserStatus = async (userId: number, status: string) => {
    try {
      await userService.updateUserStatus(userId, status);
      toast.success(`User ${status === 'inactive' ? 'deactivated' : 'activated'} successfully`);
      loadData();
    } catch (error) {
      console.error('Error updating user status:', error);
      toast.error('Failed to update user status');
    }
  };

  const handleEditUser = (user: any) => {
    // For now, show a toast message. Full edit dialog can be implemented later
    toast.info(`Edit functionality for ${user.firstName} ${user.lastName} will be available soon`);
    // TODO: Implement edit dialog
    // setEditingUser(user);
    // setOpenEditDialog(true);
  };
  
  // Filter functions
  const filterUsers = (users: UserDTO[]) => {
    return users.filter(user => {
      const matchesSearch = searchTerm === '' || 
        user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
      
      const matchesUniversity = universityFilter === 'all' || 
        (user.universityId === universityFilter);
      
      return matchesSearch && matchesStatus && matchesUniversity;
    });
  };
  
  // Get paginated data
  const getPaginatedData = (data: UserDTO[], page: number) => {
    const filtered = filterUsers(data);
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return {
      items: filtered.slice(startIndex, endIndex),
      totalPages: Math.ceil(filtered.length / ITEMS_PER_PAGE),
      totalItems: filtered.length
    };
  };


  const getStatusColor = (status: string) => {
    return status === 'Active' ? 'success' : 'default';
  };

  const userTypes = [
    { value: 'student', label: 'Student' },
    { value: 'provider', label: 'Provider' },
    { value: 'admin', label: 'Administrator' }
  ];

  // Get display data
  const studentsData = getPaginatedData(students, studentsPage);
  const providersData = getPaginatedData(providers, providersPage);
  const adminsData = getPaginatedData(admins, adminsPage);

  const TabPanel = ({ children, value, index }: any) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ mt: 3 }}>{children}</Box>}
    </div>
  );

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Psychology Profile System - User Management
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'secondary.main' }}>
              {user?.firstName?.charAt(0) || 'U'}
            </Avatar>
            <Typography variant="body1">
              {user?.firstName} {user?.lastName}
            </Typography>
            <Button color="inherit" onClick={handleLogout}>
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button variant="outlined" onClick={goBack}>
              ← Back to Dashboard
            </Button>
            <Typography variant="h4">
              User Management
            </Typography>
          </Box>
          <Button variant="contained" onClick={() => setOpenDialog(true)}>
            Add New User
          </Button>
        </Box>

        {/* Filter Controls */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setSearchTerm('')} size="small">
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e: SelectChangeEvent) => setStatusFilter(e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {isSuperAdmin() && universities.length > 0 && (
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>University</InputLabel>
                    <Select
                      value={universityFilter}
                      onChange={(e: SelectChangeEvent<number | 'all'>) => 
                        setUniversityFilter(e.target.value as number | 'all')
                      }
                      label="University"
                    >
                      <MenuItem value="all">All Universities</MenuItem>
                      {universities.map((uni) => (
                        <MenuItem key={uni.id} value={uni.id}>
                          {uni.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                {loading ? (
                  <CircularProgress />
                ) : (
                  <>
                    <Typography variant="h3" color="primary">
                      {filterUsers(students).length}
                    </Typography>
                    <Typography variant="body1">
                      Total Students
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                {loading ? (
                  <CircularProgress />
                ) : (
                  <>
                    <Typography variant="h3" color="secondary">
                      {filterUsers(providers).length}
                    </Typography>
                    <Typography variant="body1">
                      Active Providers
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                {loading ? (
                  <CircularProgress />
                ) : (
                  <>
                    <Typography variant="h3" color="success">
                      {filterUsers(admins).length}
                    </Typography>
                    <Typography variant="body1">
                      System Admins
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* User Tables */}
        <Card>
          <CardContent>
            <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
              <Tab label="Students" />
              <Tab label="Providers" />
              <Tab label="Administrators" />
            </Tabs>

            {/* Students Tab */}
            <TabPanel value={currentTab} index={0}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <TableContainer component={Paper} elevation={0}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>School ID</TableCell>
                          <TableCell>Program</TableCell>
                          <TableCell>Provider</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Last Login</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {studentsData.items.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} align="center">
                              No students found
                            </TableCell>
                          </TableRow>
                        ) : (
                          studentsData.items.map((student) => (
                            <TableRow key={student.userId}>
                              <TableCell>
                                <Box>
                                  <Typography variant="body1">
                                    {student.firstName} {student.lastName}
                                  </Typography>
                                  <Typography variant="body2" color="textSecondary">
                                    {student.email}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>{student.schoolId || '-'}</TableCell>
                              <TableCell>{student.program || '-'}</TableCell>
                              <TableCell>{student.assignedProviderName || '-'}</TableCell>
                              <TableCell>
                                <Chip 
                                  label={student.status} 
                                  color={getStatusColor(student.status === 'active' ? 'Active' : 'Inactive') as any}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                {student.lastLogin ? 
                                  new Date(student.lastLogin).toLocaleDateString() : '-'
                                }
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Button 
                                    variant="outlined" 
                                    size="small"
                                    onClick={() => handleEditUser(student)}
                                  >
                                    Edit
                                  </Button>
                                  <Button 
                                    variant="outlined" 
                                    size="small" 
                                    color={student.status === 'active' ? 'error' : 'success'}
                                    onClick={() => handleUpdateUserStatus(
                                      student.userId, 
                                      student.status === 'active' ? 'inactive' : 'active'
                                    )}
                                  >
                                    {student.status === 'active' ? 'Deactivate' : 'Activate'}
                                  </Button>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {studentsData.totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <Pagination 
                        count={studentsData.totalPages} 
                        page={studentsPage} 
                        onChange={(e, page) => setStudentsPage(page)}
                        color="primary"
                      />
                    </Box>
                  )}
                </>
              )}
            </TabPanel>

            {/* Providers Tab */}
            <TabPanel value={currentTab} index={1}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <TableContainer component={Paper} elevation={0}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Department</TableCell>
                          <TableCell>Students</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Last Login</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {providersData.items.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} align="center">
                              No providers found
                            </TableCell>
                          </TableRow>
                        ) : (
                          providersData.items.map((provider) => (
                            <TableRow key={provider.userId}>
                              <TableCell>
                                <Box>
                                  <Typography variant="body1">
                                    {provider.firstName} {provider.lastName}
                                  </Typography>
                                  <Typography variant="body2" color="textSecondary">
                                    {provider.email}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>{provider.department || '-'}</TableCell>
                              <TableCell>{provider.studentCount || 0}</TableCell>
                              <TableCell>
                                <Chip 
                                  label={provider.status} 
                                  color={getStatusColor(provider.status === 'active' ? 'Active' : 'Inactive') as any}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                {provider.lastLogin ? 
                                  new Date(provider.lastLogin).toLocaleDateString() : '-'
                                }
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Button 
                                    variant="outlined" 
                                    size="small"
                                    onClick={() => handleEditUser(provider)}
                                  >
                                    Edit
                                  </Button>
                                  <Button variant="outlined" size="small">
                                    View Students
                                  </Button>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {providersData.totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <Pagination 
                        count={providersData.totalPages} 
                        page={providersPage} 
                        onChange={(e, page) => setProvidersPage(page)}
                        color="primary"
                      />
                    </Box>
                  )}
                </>
              )}
            </TabPanel>

            {/* Administrators Tab */}
            <TabPanel value={currentTab} index={2}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <TableContainer component={Paper} elevation={0}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Role</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Last Login</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {adminsData.items.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              No administrators found
                            </TableCell>
                          </TableRow>
                        ) : (
                          adminsData.items.map((admin) => (
                            <TableRow key={admin.userId}>
                              <TableCell>
                                <Box>
                                  <Typography variant="body1">
                                    {admin.firstName} {admin.lastName}
                                  </Typography>
                                  <Typography variant="body2" color="textSecondary">
                                    {admin.email}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                {isSuperAdmin() && admin.adminType === 1 ? 'Super Administrator' : 'Administrator'}
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={admin.status} 
                                  color={getStatusColor(admin.status === 'active' ? 'Active' : 'Inactive') as any}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                {admin.lastLogin ? 
                                  new Date(admin.lastLogin).toLocaleDateString() : '-'
                                }
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Button 
                                    variant="outlined" 
                                    size="small"
                                    onClick={() => handleEditUser(admin)}
                                  >
                                    Edit
                                  </Button>
                                  <Button 
                                    variant="outlined" 
                                    size="small" 
                                    color="error"
                                    disabled={adminsData.items.length === 1}
                                  >
                                    Remove
                                  </Button>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {adminsData.totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <Pagination 
                        count={adminsData.totalPages} 
                        page={adminsPage} 
                        onChange={(e, page) => setAdminsPage(page)}
                        color="primary"
                      />
                    </Box>
                  )}
                </>
              )}
            </TabPanel>
          </CardContent>
        </Card>

        {/* Enhanced Add User Dialog */}
        <EnhancedAddUserDialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          onSuccess={loadData}
          userUniversityId={user?.universityId}
          userType={user?.userType || ''}
          universities={universities}
          providers={providers}
        />
      </Container>
    </>
  );
};

export default UserManagementPage;